<script lang="ts">
export default {
  name: 'CompaniesReviewReplyApprovalFlowForm',
}
</script>

<script setup lang="ts">
import { computed } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { useField } from 'vee-validate'
import { useRoute, useRouter } from 'vue-router'
import { KkLoading, KkRadioGroup, KkButton, KkBox } from 'src/biz_spa/components/base'
import { KkInfo } from 'src/biz_spa/components/ui'
import {storeToRefs} from 'pinia'
import {useCompaniesReviewReplyApprovalFlowStore} from 'src/biz_spa/domains/review/stores'
type Company = {
  attributes: {
    reply_approval_flow: boolean,
  }
}

type Payload = {
  company_uuid: string,
  values: {
    biz_companies_review_form: {
      approval_status: string
    }
  }
}
const store = useStore()
const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const replyStore = useCompaniesReviewReplyApprovalFlowStore()
const {
  isLoading
} = storeToRefs(replyStore)
const {
  updateReviewReplyApprovalFlow
} = replyStore
const companyUuid = computed<string>(() => route.params.company_uuid as string)
const company = computed<Company>(() => store.state.companiesCompany.data)
const companyLoading = computed<boolean>(() => store.state.companiesCompany.loading)
const requestingCount = computed<number>(() => store.state.companiesReviews.requestingCount)
const rejectedCount = computed<number>(() => store.state.companiesReviews.rejectedCount)
const reviewLoading = computed<boolean>(() => store.state.companiesReviews.loading)
const { value: approvalFlow, handleChange: approvalFlowHandleChange } = useField('approvalFlow', undefined, { initialValue: false })
const readCompany = (): Promise<void> => store.dispatch('companiesCompany/readCompany', companyUuid.value)
const readReviews = (payload: Payload): Promise<void> => store.dispatch('companiesReviews/readReviews', payload)
const approvalFlowOptions = [
  {
    id: 'on',
    value: true,
    text: t('pages.companies.reviewReplyApprovalFlowForm.on'),
  },
  {
    id: 'off',
    value: false,
    text: t('pages.companies.reviewReplyApprovalFlowForm.off'),
  },
]

const onSubmit = async (): Promise<void> => {
  await readReviews({company_uuid: companyUuid.value, values: { biz_companies_review_form: { approval_status: 'all' } }})
  if (!approvalFlow.value && (requestingCount.value !== 0 || rejectedCount.value !== 0)) {
    alert(t('pages.companies.reviewReplyApprovalFlowForm.invalidAlert'))
    return
  }
  const isSuccess = await updateReviewReplyApprovalFlow({companyUuid: companyUuid.value, params: { replyApprovalFlow: approvalFlow.value }})
  if(isSuccess) router.push({ name: 'CompaniesManagement', params: { company_uuid: companyUuid.value } })
}
const load = async (): Promise<void> => {
  await readCompany()
  approvalFlow.value = company.value.attributes.reply_approval_flow
}
load()
</script>
<template>
  <KkBox>
    <template #content>
      <div class="p-box__body">
        <div class="p-box">
          <div class="p-box__header">
            <p class="p-box__title">
              {{ $t('pages.companies.reviewReplyApprovalFlowForm.header') }}
            </p>
          </div>
          <div class="p-box__body">
            <KkInfo>
              {{ $t('pages.companies.reviewReplyApprovalFlowForm.info') }}
            </KkInfo>
            <KkLoading v-if="companyLoading || isLoading || reviewLoading" />
            <div v-else class="form">
              <KkRadioGroup
                :model-value="approvalFlow"
                :items="approvalFlowOptions"
                class="c-radio__input"
                :name="$t('pages.companies.reviewReplyApprovalFlowForm.header')"
                @update:model-value="approvalFlowHandleChange"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <div class="buttons">
        <div class="buttonWrapper">
          <KkButton
            color="secondary"
            :to="{ name: 'CompaniesManagement', params: { company_uuid: companyUuid } }"
            :text="$t('labels.cancel')"
          />
        </div>
        <div class="buttonWrapper">
          <KkButton
            color="primary"
            :text="$t('labels.save')"
            @click="onSubmit()"
          />
        </div>
      </div>
    </template>
  </KkBox>
</template>
<style lang="scss" scoped>
.buttons {
  margin-top: 32px;
  display: flex;
  justify-content: flex-end;
}
.buttonWrapper:not(:first-child) {
  margin-left: 8px;
}
.form {
  margin-top: 10px;
}
</style>
